// alert-system.js - Premium Custom Design (Framework Independent)
// Optimized: Single style injection, efficient event handling, better accessibility

// ============================================================
// 1. Configuration & State
// ============================================================
const CONFIG = {
  autoHideDelay: 3000, // for non-loading alerts
  loadingAutoHideDelay: 9000, // safety fallback for loading alerts
  animationDuration: 300, // matches CSS transition
  maxStackedAlerts: 5, // limit to prevent off-screen
  stackOffset: 80, // vertical spacing between stacked alerts
};

const state = {
  activeAlerts: new Set(), // track active alert elements
  stackedCount: 0,
  listenersInitialized: false,
  styleInjected: false,
  hideTimeout: null,
};

// ============================================================
// 2. Style Injection (Single Source)
// ============================================================
const ALERT_STYLES = `
  /* ----- Alert & Toast System ----- */
  .custom-alert {
    position: fixed;
    top: 24px;
    left: 50%;
    transform: translateX(-50%) translateY(-20px);
    min-width: 300px;
    max-width: 500px;
    width: 90%;
    z-index: 9999;
    opacity: 0;
    visibility: hidden;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none; /* allow clicks to pass through when hidden */
  }
  .custom-alert.alert-show {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
    pointer-events: auto;
  }

  .alert-content {
    background: #fff;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 20px 40px rgba(0,0,0,0.10),
                0 8px 16px rgba(0,0,0,0.06),
                0 0 0 1px rgba(0,0,0,0.04),
                inset 0 1px 0 rgba(255,255,255,0.8);
    border-left: 4px solid var(--alert-color, #7c3aed);
    position: relative;
    overflow: hidden;
    pointer-events: auto;
  }

  .alert-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .alert-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .alert-title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.4;
  }
  .alert-close-btn {
    background: rgba(0,0,0,0.05);
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s ease;
    flex-shrink: 0;
    padding: 0;
  }
  .alert-close-btn:hover {
    background: rgba(0,0,0,0.10);
    color: #374151;
    transform: scale(1.05);
  }
  .alert-close-btn:active {
    transform: scale(0.95);
  }
  .alert-close-btn svg {
    width: 14px;
    height: 14px;
  }
  .alert-body {
    color: #4b5563;
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 8px;
  }
  .alert-body strong {
    font-weight: 500;
    color: #374151;
  }

  /* ----- Spinner (for all alerts) ----- */
  .alert-spinner {
    width: 20px;
    height: 20px;
    border: 3px solid var(--alert-light, #ede9fe);
    border-top-color: var(--alert-color, #7c3aed);
    border-radius: 50%;
    animation: alertSpinner 1s linear infinite;
    flex-shrink: 0;
  }
  @keyframes alertSpinner {
    to { transform: rotate(360deg); }
  }

  /* ----- Variant overrides (color mapping via CSS custom properties) ----- */
  .custom-alert-danger  { --alert-color: #dc2626; --alert-light: #fee2e2; --alert-dark: #b91c1c; }
  .custom-alert-success { --alert-color: #059669; --alert-light: #d1fae5; --alert-dark: #047857; }
  .custom-alert-warning { --alert-color: #d97706; --alert-light: #fef3c7; --alert-dark: #b45309; }
  .custom-alert-info    { --alert-color: #2563eb; --alert-light: #dbeafe; --alert-dark: #1d4ed8; }
  .custom-alert-primary { --alert-color: #7c3aed; --alert-light: #ede9fe; --alert-dark: #6d28d9; }

  /* ----- Stacked alerts ----- */
  .custom-alert-stack {
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9998;
    width: 300px;
    pointer-events: none;
    transition: all 0.3s ease;
  }
  .custom-alert-stack .alert-content {
    pointer-events: auto;
    margin-bottom: 12px;
    transform: scale(0.95);
    opacity: 0.9;
  }

  /* ----- Toasts ----- */
  .custom-toast {
    position: fixed;
    bottom: 24px;
    right: 24px;
    min-width: 200px;
    max-width: 350px;
    background: #fff;
    border-radius: 12px;
    padding: 14px 18px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
    border-left: 3px solid var(--toast-color, #7c3aed);
    z-index: 9997;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
  }
  .custom-toast.toast-show {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
  .toast-message {
    color: #374151;
    font-size: 14px;
    line-height: 1.4;
  }

  /* ----- Dark mode ----- */
  @media (prefers-color-scheme: dark) {
    .alert-content, .custom-toast {
      background: #1f2937;
      box-shadow: 0 20px 40px rgba(0,0,0,0.30),
                  0 8px 16px rgba(0,0,0,0.20);
    }
    .alert-title { color: #f9fafb; }
    .alert-body { color: #d1d5db; }
    .alert-body strong { color: #e5e7eb; }
    .alert-close-btn {
      background: rgba(255,255,255,0.10);
      color: #9ca3af;
    }
    .alert-close-btn:hover {
      background: rgba(255,255,255,0.15);
      color: #d1d5db;
    }
    .toast-message { color: #f3f4f6; }
  }

  /* ----- Responsive ----- */
  @media (max-width: 640px) {
    .custom-alert {
      width: 95%;
      min-width: unset;
      top: 16px;
    }
    .alert-content { padding: 16px; }
    .alert-title { font-size: 15px; }
    .alert-body { font-size: 13px; }
    .custom-toast {
      left: 16px;
      right: 16px;
      bottom: 16px;
      max-width: none;
    }
  }

  /* ----- Print ----- */
  @media print {
    .custom-alert, .custom-toast, .custom-alert-stack { display: none !important; }
  }
`;

function injectStyles() {
  if (state.styleInjected) return;
  const styleEl = document.createElement('style');
  styleEl.id = 'alert-system-styles';
  styleEl.textContent = ALERT_STYLES;
  document.head.appendChild(styleEl);
  state.styleInjected = true;
}

// ============================================================
// 3. Global Event Listeners (singleton)
// ============================================================
function setupGlobalListeners() {
  if (state.listenersInitialized) return;

  // Close button clicks (delegated)
  document.addEventListener('click', e => {
    const closeBtn = e.target.closest('.alert-close-btn');
    if (!closeBtn) return;
    const alertEl = closeBtn.closest('.custom-alert, .custom-alert-stack');
    if (alertEl) {
      dismissAlert(alertEl);
    }
  });

  // Hide all alerts on page navigation (bfcache / pageshow)
  window.addEventListener('pageshow', () => {
    // Remove all active alerts (including stacked)
    document
      .querySelectorAll('.custom-alert, .custom-alert-stack')
      .forEach(dismissAlert);
    state.stackedCount = 0;
  });

  state.listenersInitialized = true;
}

// ============================================================
// 4. Core Alert Management
// ============================================================
function dismissAlert(element) {
  if (!element || !element.parentNode) return;
  element.style.opacity = '0';
  // Remove after animation
  setTimeout(() => {
    if (element.parentNode) {
      element.parentNode.removeChild(element);
      state.activeAlerts.delete(element);
    }
  }, CONFIG.animationDuration);
}

function createAlertElement(html, isStacked = false) {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html.trim();
  const alertEl = wrapper.firstElementChild;
  // Store a reference to its auto-dismiss timeout if any
  return alertEl;
}

function showAlertWithOptions({
  style = 'info',
  title = '',
  message = '',
  isStacked = false,
  stackPosition = 0,
  autoHide = true,
  duration = CONFIG.autoHideDelay,
  showSpinner = true,
  showClose = true,
  code = null, // special handling for 417
} = {}) {
  // Hide any existing main alert (but not stacked if we're stacking?)
  if (!isStacked) {
    // Remove all previous alerts (including stacked) to keep clean
    document
      .querySelectorAll('.custom-alert, .custom-alert-stack')
      .forEach(dismissAlert);
    state.stackedCount = 0;
  }

  injectStyles();
  setupGlobalListeners();

  const colorMap = {
    danger: { primary: '#dc2626', light: '#fee2e2', dark: '#b91c1c' },
    success: { primary: '#059669', light: '#d1fae5', dark: '#047857' },
    warning: { primary: '#d97706', light: '#fef3c7', dark: '#b45309' },
    info: { primary: '#2563eb', light: '#dbeafe', dark: '#1d4ed8' },
    primary: { primary: '#7c3aed', light: '#ede9fe', dark: '#6d28d9' },
  };
  const colors = colorMap[style] || colorMap.info;

  const spinnerHTML = showSpinner ? `<div class="alert-spinner"></div>` : '';
  const closeBtnHTML = showClose
    ? `
    <button class="alert-close-btn" aria-label="Fermer">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
        <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"/>
      </svg>
    </button>
  `
    : '';

  const msgText =
    style === 'danger' && message.message ? message.message : message;

  let alertHTML = `
    <div class="custom-alert custom-alert-${style}" 
         style="--alert-color: ${colors.primary}; --alert-light: ${colors.light}; --alert-dark: ${colors.dark};"
         role="alert" aria-live="${style === 'danger' ? 'assertive' : 'polite'}">
      <div class="alert-content">
        <div class="alert-header">
          <div class="alert-header-left">
            <div class="alert-title">${title || ''}</div>
            ${spinnerHTML}
          </div>
          ${closeBtnHTML}
        </div>
        <div class="alert-body">
          <strong>${msgText || ''}</strong>
        </div>
      </div>
    </div>
  `;

  // If stacked, adjust wrapper and position
  if (isStacked) {
    const stackId = `alert-stack-${Date.now()}`;
    const topOffset = 24 + stackPosition * CONFIG.stackOffset;
    alertHTML = `
      <div id="${stackId}" class="custom-alert-stack" style="top: ${topOffset}px;">
        ${alertHTML.replace('custom-alert', 'custom-alert-stack-item')}
      </div>
    `;
    // But we actually need the inner .custom-alert to be the alert content?
    // The stacked wrapper contains the alert content directly, so we'll adjust:
    // Actually we'll create the stacked wrapper and put the alert content inside.
    // To avoid duplication, we'll rewrite the HTML to put content directly inside the stack div.
    // Simpler: Use the same alert-content but without the outer .custom-alert div.
    // We'll create a separate branch.
  }

  // For simplicity, we'll keep the existing approach: insertAdjacentHTML
  // But we need to handle stacked differently. Since we have both functions,
  // we can just keep showStackedAlert separate. We'll refactor showAlert to handle both.

  // Given the current API, showAlert is for main alert, showStackedAlert for stacked.
  // We'll keep them separate to avoid breaking changes.
}

// ============================================================
// 5. Public API (backward compatible)
// ============================================================

/**
 * Show a main alert (replaces any existing alert)
 * @param {string} style - 'danger'|'success'|'warning'|'info'|'primary'
 * @param {string} title - Alert title
 * @param {string|object} message - Message text or error object with .message and .statusCode
 */
export const showAlert = (style, title, message) => {
  // Clean up previous alerts
  document
    .querySelectorAll('.custom-alert, .custom-alert-stack')
    .forEach(dismissAlert);
  state.stackedCount = 0;

  const isPrimary = style === 'primary';
  const msgText =
    style === 'danger' && message.message ? message.message : message;
  const code =
    style === 'danger' && message.statusCode ? message.statusCode : null;

  injectStyles();
  setupGlobalListeners();

  const colors =
    {
      danger: { primary: '#dc2626', light: '#fee2e2', dark: '#b91c1c' },
      success: { primary: '#059669', light: '#d1fae5', dark: '#047857' },
      warning: { primary: '#d97706', light: '#fef3c7', dark: '#b45309' },
      info: { primary: '#2563eb', light: '#dbeafe', dark: '#1d4ed8' },
      primary: { primary: '#7c3aed', light: '#ede9fe', dark: '#6d28d9' },
    }[style] || colors.info;

  const spinnerHTML = `<div class="alert-spinner"></div>`;
  const closeBtnHTML = isPrimary
    ? ''
    : `
    <button class="alert-close-btn" aria-label="Fermer">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
        <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"/>
      </svg>
    </button>
  `;

  const alertHTML = `
    <div id="notificationAlert" class="custom-alert custom-alert-${style}" 
         style="--alert-color: ${colors.primary}; --alert-light: ${colors.light}; --alert-dark: ${colors.dark};"
         role="alert" aria-live="${style === 'danger' ? 'assertive' : 'polite'}">
      <div class="alert-content">
        <div class="alert-header">
          <div class="alert-header-left">
            <div class="alert-title">${title || ''}</div>
            ${spinnerHTML}
          </div>
          ${closeBtnHTML}
        </div>
        <div class="alert-body">
          <strong>${msgText || ''}</strong>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('afterbegin', alertHTML);
  const alertEl = document.getElementById('notificationAlert');

  // Trigger show animation
  requestAnimationFrame(() => {
    if (alertEl) alertEl.classList.add('alert-show');
  });

  // Auto-hide (except primary/loading)
  if (!isPrimary) {
    const timeoutId = setTimeout(() => {
      dismissAlert(alertEl);
    }, CONFIG.autoHideDelay);
    // Store timeout on element for cleanup if needed
    if (alertEl) alertEl._hideTimeout = timeoutId;
  } else {
    // Safety fallback for primary alerts (in case something goes wrong)
    const safetyTimeout = setTimeout(() => {
      if (alertEl && alertEl.parentNode) {
        dismissAlert(alertEl);
      }
    }, CONFIG.loadingAutoHideDelay);
    if (alertEl) alertEl._safetyTimeout = safetyTimeout;
  }

  // Special handling for code 417
  if (code === 417) {
    const signButton = document.getElementById('sign');
    if (signButton) {
      signButton.click();
    }
  }

  // Store reference for cleanup
  if (alertEl) state.activeAlerts.add(alertEl);
};

/**
 * Hide/dismiss the current main alert and any stacked alerts
 */
export const hideAlert = () => {
  document
    .querySelectorAll('.custom-alert, .custom-alert-stack')
    .forEach(dismissAlert);
  state.stackedCount = 0;
};

/**
 * Show a stacked alert (multiple alerts at once)
 */
export const showStackedAlert = (style, title, message, position = 1) => {
  if (state.stackedCount >= CONFIG.maxStackedAlerts) {
    // Remove oldest stacked alert
    const oldest = document.querySelector('.custom-alert-stack');
    if (oldest) dismissAlert(oldest);
    state.stackedCount--;
  }

  const colors = {
    danger: '#dc2626',
    success: '#059669',
    warning: '#d97706',
    info: '#2563eb',
    primary: '#7c3aed',
  };
  const color = colors[style] || colors.info;
  const msgText =
    style === 'danger' && message.message ? message.message : message;

  const stackId = `alert-stack-${Date.now()}`;
  const topOffset = 24 + state.stackedCount * CONFIG.stackOffset;

  const markup = `
    <div id="${stackId}" class="custom-alert-stack" style="top: ${topOffset}px;">
      <div class="alert-content" style="border-left-color: ${color};">
        <div class="alert-header">
          <div class="alert-header-left">
            <div class="alert-spinner"></div>
            <div class="alert-title">${title || ''}</div>
          </div>
          <button class="alert-close-btn" aria-label="Fermer">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
              <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"/>
            </svg>
          </button>
        </div>
        <div class="alert-body">
          <strong>${msgText || ''}</strong>
        </div>
      </div>
    </div>
  `;

  injectStyles();
  setupGlobalListeners();

  document.body.insertAdjacentHTML('afterbegin', markup);
  const alertEl = document.getElementById(stackId);

  // Animate in
  requestAnimationFrame(() => {
    if (alertEl) {
      alertEl.style.opacity = '1';
      alertEl.style.transform = 'translateX(-50%) translateY(0)';
    }
  });

  // Auto-dismiss after 9 seconds
  const timeoutId = setTimeout(() => {
    dismissAlert(alertEl);
    state.stackedCount = Math.max(0, state.stackedCount - 1);
  }, CONFIG.loadingAutoHideDelay);

  if (alertEl) {
    alertEl._hideTimeout = timeoutId;
    state.activeAlerts.add(alertEl);
    state.stackedCount++;
  }
};

/**
 * Show a toast notification (bottom-right, auto-dismiss)
 */
export const showToast = (style, message, duration = 3000) => {
  const colors = {
    danger: '#dc2626',
    success: '#059669',
    warning: '#d97706',
    info: '#2563eb',
    primary: '#7c3aed',
  };
  const color = colors[style] || colors.info;
  const toastId = `toast-${Date.now()}`;

  const markup = `
    <div id="${toastId}" class="custom-toast" 
         style="--toast-color: ${color};" 
         role="status" aria-live="polite">
      <div class="toast-content">
        <div class="toast-message">${message || ''}</div>
      </div>
    </div>
  `;

  injectStyles(); // styles already include toast styles
  setupGlobalListeners();

  document.body.insertAdjacentHTML('beforeend', markup);
  const toastEl = document.getElementById(toastId);

  requestAnimationFrame(() => {
    if (toastEl) toastEl.classList.add('toast-show');
  });

  setTimeout(() => {
    if (toastEl) {
      toastEl.classList.remove('toast-show');
      setTimeout(() => {
        if (toastEl.parentNode) toastEl.parentNode.removeChild(toastEl);
      }, CONFIG.animationDuration);
    }
  }, duration);
};

// ============================================================
// 6. Expose to global (backward compatibility)
// ============================================================
window.hideAlert = hideAlert;
window.showAlert = showAlert;
window.showToast = showToast;
window.showStackedAlert = showStackedAlert;

// ============================================================
// 7. Auto-initialize on DOM ready (optional)
// ============================================================
document.addEventListener('DOMContentLoaded', () => {
  // Any existing alerts from server can be enhanced here if needed
});
